import {
  Box,
  Button,
  Link,
  Stack,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import imgList from "./mockImages";
import { samples } from "./speciesJSON";
import { keyboardKeys } from "./keyboardDesign";
import { mockAudioUrls } from "./mockContent";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";

interface ISample {
  keyboardKey?: string;
  name?: String;
  index?: number;
  type?: string;
  audioSample?: string;
  url?: string;
}
type AudioType = { [index: string]: HTMLAudioElement };

interface IProps {
  kill: () => void;
}

export const Sampler = ({ kill = () => {} }: IProps) => {
  const keyboardKeyList = [
    "Q",
    "W",
    "E",
    "R",
    "T",
    "Y",
    "U",
    "I",
    "O",
    "P",
    "A",
    "S",
    "D",
    "F",
    "G",
    "H",
    "J",
    "K",
    "L",
    "Z",
    "X",
    "C",
    "V",
    "B",
    "N",
    "M",
  ];

  // SWYgYW55b25lIGV2ZXIgcmVhZHMgdGhpcywgYW5kIEknbGwgYnkgeW91IGEgY2FzZSBvZiBiZWVyIDotKSAvIEFuZGVycyBOaWxvdQ==
  const [audioFiles, setAudioFiles] = useState<AudioType>({});
  const [activeSample, setActiveSample] = useState<string[]>([""]);
  const [audioAmountLoaded, setAudioAmountLoaded] = useState(0);
  const [showRotate, setShowRotate] = useState(false);

  const handleWindowResize = () => {
    if (window.innerHeight > window.innerWidth) {
      setShowRotate(true);
    } else {
      setShowRotate(false);
    }
  };
  useEffect(() => {
    handleWindowResize();
    window.addEventListener("resize", () => {
      handleWindowResize();
    });
    return () => {
      window.removeEventListener("keydown", keyDown);
    };
  }, []);

  useEffect(() => {
    if (audioAmountLoaded >= keyboardKeyList.length) {
      window.addEventListener("keydown", keyDown);
      return () => {
        window.removeEventListener("keydown", keyDown);
      };
    }
  }, [audioAmountLoaded]);

  useEffect(() => {
    const preloadImages = () => {
      imgList.forEach((imageSrc) => {
        const img = new Image();
        img.src = imageSrc;
      });
    };

    preloadImages();
  }, []);

  const loadAudioElements = async () => {
    try {
      mockAudioUrls.forEach(async (elm, index) => {
        const t = await new Promise(async () => {
          const response = await fetch(elm.path);
          const blob = await response.blob();
          const audioUrl = URL.createObjectURL(blob);
          const audio = new Audio(audioUrl);

          setAudioFiles((prevState) => ({
            ...prevState,
            [elm.key]: audio,
          }));
          setAudioAmountLoaded(index + 1);
        });
      });
    } catch (error) {
      console.error("Error loading mock files:", error);
    }
  };

  useEffect(() => {
    loadAudioElements();
  }, []);

  const keyDown = (e: KeyboardEvent) => {
    if (String(e.key).toUpperCase() === "ESCAPE") {
      unMountSampler();
      return;
    }

    if (
      keyboardKeyList.includes(e.key.toUpperCase()) &&
      !activeSample.includes(e.key.toUpperCase())
    ) {
      triggerSample(e.key.toUpperCase());
    }
  };

  const unMountSampler = () => {
    kill();
  };

  const triggerSample = (key: string) => {
    if (String(key).toUpperCase() === "ESC") {
      unMountSampler();
      return;
    }

    let activeSampleOld = [...activeSample];
    activeSampleOld.unshift(key);
    setActiveSample(activeSampleOld);

    const audio: HTMLAudioElement = audioFiles[key as string];

    audio.currentTime = 0;
    audio.play();
  };

  interface IKeyboardKey {
    key: string;
    classes: string[];
  }

  const getClasses = (classNames: string[]) => {
    let test: SxProps<Theme> = { ...sxStyles.key };

    classNames.forEach((className) => {
      const t = sxStyles[className];
      if (className === "key") {
        test = { ...test, ...sxStyles[className] } as SxProps<Theme>;
      }
      if (className === "tab") {
        test = { ...test, ...sxStyles[className] } as SxProps<Theme>;
      }
      if (className === "backslash") {
        test = { ...test, ...sxStyles[className] } as SxProps<Theme>;
      }
      if (className === "capslock") {
        test = { ...test, ...sxStyles[className] } as SxProps<Theme>;
      }
      if (className === "return") {
        test = { ...test, ...sxStyles[className] } as SxProps<Theme>;
      }
      if (className === "leftshift") {
        test = { ...test, ...sxStyles[className] } as SxProps<Theme>;
      }
      if (className === "rightshift") {
        test = { ...test, ...sxStyles[className] } as SxProps<Theme>;
      }
      if (className === "command") {
        test = { ...test, ...sxStyles[className] } as SxProps<Theme>;
      }
      if (className === "space") {
        test = { ...test, ...sxStyles[className] } as SxProps<Theme>;
      }
    });
    return test;
  };

  const getName = (sample: ISample) => {
    const nameArr = String(sample?.name)
      .toUpperCase()
      .split(String(sample.keyboardKey));
    const string = nameArr.map((part: string, index: number) => {
      return (
        <Stack key={`keyboard-key-${index}`} direction={"row"}>
          <Stack direction={"row"}>{part}</Stack>
          <Stack sx={sxStyles.colorPrimary} direction={"row"}>
            {index < nameArr.length - 1 ? sample.keyboardKey : ""}
          </Stack>
        </Stack>
      );
    });
    return string;
  };
  return (
    <Box sx={sxStyles.wrapper}>
      {showRotate ? (
        <ScreenRotationIcon sx={sxStyles.rotateIcon} color="primary" />
      ) : (
        <>
          <Box sx={sxStyles.imgWrapper}>
            {samples.map((sample: ISample, index: number) => (
              <Box
                key={`bgImg-${index}`}
                sx={
                  {
                    ...sxStyles.bgImg,
                    ...(activeSample[0] ===
                    String(sample.keyboardKey).toUpperCase()
                      ? sxStyles.show
                      : {}),
                  } as SxProps<Theme>
                }
              >
                {activeSample[0] ===
                  String(sample.keyboardKey).toUpperCase() && (
                  <>
                    <Typography sx={sxStyles.title} variant="h1">
                      <Link
                        href={sample.url}
                        sx={sxStyles.titleLink}
                        target="_blank"
                      >
                        <Stack direction={"row"}>{getName(sample)}</Stack>
                      </Link>
                    </Typography>
                    <Box
                      sx={sxStyles.bgImgContainer}
                      style={{ backgroundImage: `url(${imgList[index]})` }}
                    />
                  </>
                )}
              </Box>
            ))}
          </Box>
          <Box sx={sxStyles.keyboardBase}>
            {keyboardKeys.map((keyboardKey: IKeyboardKey, index: number) => {
              return (
                <Button
                  variant="outlined"
                  onClick={() =>
                    triggerSample(String(keyboardKey.key).toUpperCase())
                  }
                  key={`keyboard-key-${index}`}
                  disabled={keyboardKey.key.length === 0}
                  sx={{
                    background:
                      keyboardKey.key.length > 0 ? "rgba(255,255,255,0.2)" : "",
                    ...getClasses(keyboardKey.classes),
                  }}
                >
                  {keyboardKey.key}
                </Button>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

const sxStyles: { [key: string]: SxProps<Theme> } = {
  "@keyframes wobble": {
    "0%": {
      transform: "scale(.9)",
    },
    "50%": {
      transform: "scale(1.1)",
    },
    "100%": {
      transform: "scale(.9)",
    },
  },
  rotateIcon: (theme: Theme) => ({
    animation: `$wobble 1000ms ${theme.transitions.easing.easeInOut}`,
    animationIterationCount: "infinite",
    fontSize: "50vw",
  }),
  mainTitle: {
    color: "white.main",
    position: "absolute",
    top: "0",
    fontSize: "70px",
    textTransform: "uppercase",
    zIndex: "-1",
  },

  title: {
    color: "white.main",
    position: "absolute",
    top: "0",
    textTransform: "uppercase",
    zIndex: 1,
    pl: 2,
  },
  titleLink: (theme: Theme) => ({
    color: "white.main",
    textDecoration: "underline",
    transition: `${theme.transitions.duration.short}ms color ${theme.transitions.easing.easeInOut}`,
    "&:hover": {
      color: theme.palette.secondary.main + "!important",
    },
  }),
  imgWrapper: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: "-1",
  },
  bgImg: (theme: Theme) => ({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0,
    transition: `opacity ${theme.transitions.easing.sharp} 50ms`,
    pointerEvents: "none",
  }),
  bgImgContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.5,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  show: {
    opacity: 1,
    pointerEvents: "all",
  },
  wrapper: {
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    position: "fixed",
    zIndex: "10001",
    backgroundColor: "white.main",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "space-around",
  },
  keyboardBase: (theme: Theme) => ({
    padding: "20px",
    display: "grid",
    gridGap: "5px",
    gridTemplateColumns: { xs: "repeat(8, 1fr)", sm: "repeat(29, 1fr)" },
    gridTemplateRows: { xs: "repeat(4, 1fr)", sm: "repeat(5, 1fr)" },
    transition: `max-width ${theme.transitions.duration.complex}ms`,
    width: { xs: "100vw", sm: "100%" },
    maxWidth: { sm: "800px", md: "1100px" },
  }),
  key: {
    borderRadius: "3px",
    gridColumn: "span 2",
    textAlign: "center",
    pt: 2.125,
    py: { sm: 1.25, md: 2.5 },
    px: { md: 2.25 },
    minWidth: 0,
    transition: `padding ${200}ms, font-size ${200}ms`,
    fontSize: { sm: "15px", md: "18px" },
  },
  colorPrimary: {
    color: "primary.main",
  },
  disabled: {
    display: { xs: "none", sm: "initial" },
  },
  tab: {
    gridColumn: "span 3",
  },
  backslash: {
    gridColumn: "span 3",
  },
  capslock: {
    gridColumn: "span 4",
  },
  return: {
    gridColumn: "span 3",
  },
  command: {
    gridColumn: "span 3",
  },
  leftshift: {
    gridColumn: "span 3",
  },
  rightshift: {
    gridColumn: "span 4",
  },
  space: {
    gridColumn: "span 11",
  },
  buttonWrapper: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
  },
  button: {
    display: "flex",
    flexDirection: "column",
    width: "50px",
    height: "50px",
    minWidth: "0",
    border: "1px solid grey",
    borderRadius: "4px",
    flexGrow: "inherit",
    margin: "2px",
  },
  buttonKey: {
    textOrientation: "upright",
    writingMode: "vertical-rl",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    color: "primary.main",
  },
};
